<template>
  <a
    v-if="isExternalLink"
    :href="href"
    :class="buttonClasses"
    v-on="$listeners"
  >
    <slot />
  </a>

  <router-link
    v-else-if="href"
    :to="href"
    :class="buttonClasses"
    v-on="$listeners"
    @click.native="scrollFix(href)"
  >
    <slot />
  </router-link>

  <div
    v-else
    class="relative"
    :class="widthClasses"
  >
    <button
      :disabled="loading || disabled"
      class="w-full"
      :class="buttonClasses"
      :type="type"
      v-on="$listeners"
    >
      <slot />
    </button>

    <div
      class="absolute left-0 top-0 w-full h-full flex-col items-center justify-center"
      :class="[loading ? 'flex' : 'hidden']"
    >
      <span class="loading-text text-center text-sm">Loading</span>
      <div class="lh-loading-animation relative flex-shrink-0 h-3 w-full mt-1 bullets-3">
        <div class="lh-loading lh-loading_1 absolute w-3 h-3 top-0" />
        <div class="lh-loading lh-loading_2 absolute w-3 h-3" />
        <div class="lh-loading lh-loading_3 absolute w-3 h-3" />
      </div>
    </div>
  </div>
</template>

<script>
import { startsWith } from 'lodash'

import Vue from 'vue'
import getConfig from '~branding'

export default Vue.extend({
  props: {
    variant: {
      type: String,
      default: 'primary-border',
      validator: (val) => ['primary-border', 'secondary'].includes(val),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    href: {
      type: [String, Object],
      default: '',
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: undefined,
    },
  },
  computed: {
    isExternalLink() {
      if (typeof (this.href) === 'string') {
        return startsWith(this.href, 'http')
      }
      return false
    },
    buttonClasses() {
      return [
        'text-base font-normal md:w-full xl:text-lg py-3 px-6 focus:outline-none',
        `btn-${this.variant}`,
        this.disabled ? 'disabled' : '',
        this.loading ? 'opacity-25 disabled' : '',
        this.widthClasses,
      ]
    },
    widthClasses() {
      return [
        this.fullWidth ? 'w-full' : 'md:max-w-64',
      ]
    },
    toHashtag() {
      if (typeof (this.href) === 'string') {
        return this.href.charAt(0) === '#'
      }
      return false
    },
  },
  methods: {
    // So that second use of the same hashtag triggers the url change (https://forum.vuejs.org/t/how-to-handle-anchors-bookmarks-with-vue-router/14563)
    scrollFix(hashtag) {
      if (this.toHashtag) {
        window.scrollTo(0, document.querySelector(hashtag).offsetTop - getConfig().header.offset)
      }
    },
  },
})
</script>

<style lang="scss" scoped>
@mixin button-focus {
  content: '';
  position: absolute;
  left: -6px;
  top: -6px;
  width: calc(100% + 12px);
  height: calc(100% + 12px);
  box-shadow: 0 0 0.6rem 0.1rem #52627c;

  @apply rounded;
}

.btn-primary-border {
  @apply text-primary rounded border-2 border-primary font-bold text-center inline-block relative;

  &:hover {
    @apply bg-primary text-white;
  }

  &:focus {
    &::after {
      @include button-focus;
    }
  }
}

.btn-secondary {
  @apply text-primary bg-secondary border-2 border-transparent rounded font-bold text-center inline-block relative;

  &:hover {
    @apply bg-secondary_hover;
  }

  &:focus {
    @apply bg-secondary_hover;

    &::after {
      @include button-focus;
    }
  }
}

button.disabled {
  @apply bg-gray-150 text-gray-300 border-gray-150 cursor-not-allowed;

  &:hover {
    @apply bg-gray-150 text-gray-300 border-gray-150;
  }

  &:focus {
    @apply bg-gray-150;

    &::after {
      display: none;
    }
  }
}

.lh-loading-animation {
  &.bullets-3 {
    width: 65px;
  }

  .lh-loading {
    position: absolute;
    top: 0;
    width: 12px;
    height: 12px;
    animation-name: bounce_lh-loading;
    -o-animation-name: bounce_lh-loading;
    -ms-animation-name: bounce_lh-loading;
    -webkit-animation-name: bounce_lh-loading;
    -moz-animation-name: bounce_lh-loading;
    animation-duration: 1.5s;
    -o-animation-duration: 1.5s;
    -ms-animation-duration: 1.5s;
    -webkit-animation-duration: 1.5s;
    -moz-animation-duration: 1.5s;
    animation-iteration-count: infinite;
    -o-animation-iteration-count: infinite;
    -ms-animation-iteration-count: infinite;
    -webkit-animation-iteration-count: infinite;
    -moz-animation-iteration-count: infinite;
    animation-direction: normal;
    -o-animation-direction: normal;
    -ms-animation-direction: normal;
    -webkit-animation-direction: normal;
    -moz-animation-direction: normal;
    border-radius: 12px;
    -o-border-radius: 12px;
    -ms-border-radius: 12px;
    -webkit-border-radius: 12px;
    -moz-border-radius: 12px;
    transform: scale(0.6);
    -o-transform: scale(0.6);
    -ms-transform: scale(0.6);
    -webkit-transform: scale(0.6);
    -moz-transform: scale(0.6);
  }

  .lh-loading_1 {
    left: 0;
    animation-delay: 0.6s;
    -o-animation-delay: 0.6s;
    -ms-animation-delay: 0.6s;
    -webkit-animation-delay: 0.6s;
    -moz-animation-delay: 0.6s;
  }

  .lh-loading_2 {
    left: 25px;
    animation-delay: 0.75s;
    -o-animation-delay: 0.75s;
    -ms-animation-delay: 0.75s;
    -webkit-animation-delay: 0.75s;
    -moz-animation-delay: 0.75s;
  }

  .lh-loading_3 {
    left: 50px;
    animation-delay: 0.9s;
    -o-animation-delay: 0.9s;
    -ms-animation-delay: 0.9s;
    -webkit-animation-delay: 0.9s;
    -moz-animation-delay: 0.9s;
  }
}

@keyframes bounce_lh-loading {
  0% {
    -webkit-transform: scale(0.6);
    transform: scale(0.6);
    background-color: gainsboro;
  }

  33% {
    -webkit-transform: scale(1);
    transform: scale(1);
    background-color: #ffb300;
  }

  100% {
    -webkit-transform: scale(0.6);
    transform: scale(0.6);
    background-color: gainsboro;
  }
}

@-webkit-keyframes bounce_lh-loading {
  0% {
    -webkit-transform: scale(0.6);
    background-color: gainsboro;
  }

  33% {
    -webkit-transform: scale(1);
    background-color: #ffb300;
  }

  100% {
    -webkit-transform: scale(0.6);
    background-color: gainsboro;
  }
}
</style>
